import React from 'react';
import { motion } from 'framer-motion';

// Floating animation for decorative elements
export const FloatingElement = ({ 
  children, 
  className = '', 
  duration = 6,
  distance = 20,
  delay = 0
}) => {
  return (
    <motion.div
      className={className}
      animate={{
        y: [-distance/2, distance/2, -distance/2]
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay
      }}
    >
      {children}
    </motion.div>
  );
};

// Rotating glow orb
export const GlowOrb = ({ 
  size = 200, 
  color = 'rgba(47, 128, 255, 0.3)',
  className = '',
  blur = 60
}) => {
  return (
    <motion.div
      className={`absolute rounded-full pointer-events-none ${className}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        filter: `blur(${blur}px)`
      }}
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.5, 0.8, 0.5]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    />
  );
};

// Animated grid background
export const GridBackground = ({ className = '' }) => {
  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />
      {/* Gradient mask for fade effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, #0B1220 70%)'
        }}
      />
    </div>
  );
};

// Animated particles
export const ParticleField = ({ count = 20, className = '' }) => {
  const particles = React.useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5
    }));
  }, [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-white/20"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size
          }}
          animate={{
            y: [-20, 20, -20],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: particle.delay
          }}
        />
      ))}
    </div>
  );
};

// Animated border gradient
export const AnimatedBorder = ({ children, className = '' }) => {
  return (
    <div className={`relative p-[1px] rounded-2xl overflow-hidden ${className}`}>
      {/* Animated gradient border */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(90deg, #2F80FF, #2DD4BF, #F59E0B, #2F80FF)',
          backgroundSize: '300% 100%'
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'linear'
        }}
      />
      {/* Content */}
      <div className="relative bg-[#0B1220] rounded-2xl">
        {children}
      </div>
    </div>
  );
};

export default FloatingElement;
